import React from "react";
import { Row, Col, Button, Popconfirm } from "antd";
import { QuestionComponent } from "./QnaComponents";
import Text from "components/input-types/Text";
import YesNo from "components/input-types/YesNo";
import TextArea from "components/input-types/TextArea";
import DatePicker from "components/input-types/DatePicker";
import TimePicker from "components/input-types/TimePicker";
import Checkbox from "components/input-types/Checkbox";
import RadioGroup from "components/input-types/RadioGroup";
import TreeSelect from "components/input-types/TreeSelect";
import Select from "components/input-types/Select";
import Label from "components/input-types/Label";
import FileUploader from "components/input-types/FileUploader";
import Cascader from "components/input-types/Cascader";
import AutoComplete from "components/input-types/AutoComplete";
import NumberDisplay from "components/NumberDisplay";
import _ from "lodash";
import {
	TotalFundingSource,
	TotalFundingDifference,
} from "features/capex/DataComponents";
import AssetRc from "features/capex/assetRc/AssetRc";
import {
	AssetCriticality,
	OverallRiskAssetFailure,
	OverallRiskConsequences,
} from "features/capex/npi/RiskMatrix";
import MajorCategory from "features/capex/assetCategory/MajorCategory";
import MinorCategory from "features/capex/assetCategory/MinorCategory";
import ProjectEndDate from "features/capex/ProjectEndDate";
import Agencies from "features/roc/agency";
/*
Dumb renderer components
*/

const DATE_FORMAT_STRING = "DD/MM/YYYY";
const DATE_TIME_FORMAT_STRING = "DD/MM/YYYY HH:mm";
const TIME_FORMAT_STRING = "HH:mm";

export const QuestionTableRowDeleteButton = ({ onDelete }) => {
	return (
		<div style={{ position: "absolute", right: -10 }}>
			<Popconfirm
				title="Are you sure you want to delete this row?"
				onConfirm={onDelete}
				okText="Yes"
				cancelText="No"
			>
				<Button type="link" size="small" danger>
					x
				</Button>
			</Popconfirm>
		</div>
	);
};

export const QuestionTable = ({ question, children }) => {
	return (
		<Row
			align="middle"
			className="qna-question-container qna-question-form-item-group"
		>
			<Col span={24} className="qna-question-table">
				<span className="qna-question-table-label">
					{question.title}
				</span>
				{children}
			</Col>
		</Row>
	);
};

export const QuestionTableHeaderRow = ({ questions }) => {
	return (
		<Row>
			{questions.map((q, index) => (
				<Col
					key={index}
					className="qna-question-table-cell qna-question-table-header-cell"
					span={q.col.span}
				>
					{q.title}
				</Col>
			))}
		</Row>
	);
};

export const QuestionTableBodyRow = ({ children }) => {
	return <Row style={{ width: "calc(100% - 20px)" }}>{children}</Row>;
};

export const QuestionRow = ({ question, isMandatory, children }) => {
	//depth determines left margin of a quesiton
	let depth = question?.depth || 0;
	let labelColWidth = question?.labelColWidth || 24;
	let inputColWidth = labelColWidth === 24 ? 24 : 24 - labelColWidth;
	const rowClassName =
		question?.displayMode === "tabular"
			? "qna-question-form-item-group-tabular"
			: "qna-question-form-item-group";
	return (
		<QuestionComponent
			title={question?.title}
			description={question?.description}
			isMandatory={isMandatory}
			rowClassName={rowClassName}
			inputColWidth={inputColWidth}
			labelColWidth={labelColWidth}
			depth={depth}
			question={question}
		>
			{children}
		</QuestionComponent>
	);
};

export const QuestionTableFooterRow = ({ addNewRow }) => {
	return (
		<Row justify="end">
			<Col className="qna-question-table-footer">
				<Button type="primary" onClick={addNewRow}>
					Add New Row
				</Button>
			</Col>
		</Row>
	);
};

export const QuestionTableCell = ({ children, span, className }) => {
	return (
		<Col className={"qna-question-table-cell " + className} span={span}>
			{children}
		</Col>
	);
};

const arePropsEqual = (prevProps, nextProps) => {
	return _.isEqual(prevProps, nextProps);
};

export const Question = React.memo((props) => {
	const {
		errors,
		status,
		question,
		instanceId,
		dispatch,
		isNew,
		value,
		defaultValue,
		visible,
		tree,
		lookup,
		row,
		disabled,
		attachmentDispatch,
		saveFormDispatch,
		isFormNew,
		formId,
		...otherProps
	} = props;
	const {
		id,
		textAreaLines,
		dataType,
		inputType,
		placeholder,
		prefix,
		suffix,
		options,
	} = question;
	const commonProps = {
		...otherProps,
		name: instanceId || id,
		questionId: id,
		dispatch: dispatch,
		isNew,
		defaultValue: typeof value === "undefined" ? defaultValue : value,
		row: row,
		disabled: disabled,
		dataType: dataType || "string",
		placeholder,
	};

	//filter out inactive lookup, but keep the ones that reference the current question's value
	const filteredLookup = lookup.filter(
		(l) => !l.inactive || l.id === commonProps.defaultValue
	);

	const InputDecider = () => {
		switch (inputType) {
			case "Text":
				return (
					<>
						<Text
							{...commonProps}
							prefix={prefix}
							suffix={suffix}
							options={options}
							className="print-hide"
						/>
						<div
							className="print-show"
							style={{
								border: "1px solid #d9d9d9",
								borderRadius: "2px",
								padding: 3,
								fontSize: "12px",
								whiteSpace: "pre-line",
								wordWrap: "break-word",
								overflowWrap: "break-word",
							}}
						>
							{commonProps?.defaultValue}
						</div>
					</>
				);

			case "YesNo":
				return <YesNo {...commonProps} />;
			case "TextArea":
				return (
					<>
						<TextArea
							{...commonProps}
							rows={textAreaLines}
							className="print-hide"
						/>
						<div
							className="print-show"
							style={{
								border: "1px solid #d9d9d9",
								borderRadius: "2px",
								padding: 3,
								fontSize: "12px",
								whiteSpace: "pre-line",
								wordWrap: "break-word",
								overflowWrap: "break-word",
							}}
						>
							{commonProps?.defaultValue}
						</div>
					</>
				);
			case "DatePicker":
				return (
					<DatePicker
						{...commonProps}
						format={
							dataType === "date"
								? DATE_FORMAT_STRING
								: DATE_TIME_FORMAT_STRING
						}
					/>
				);
			case "TimePicker":
				return (
					<TimePicker {...commonProps} format={TIME_FORMAT_STRING} />
				);
			case "CheckBox":
				return (
					<Checkbox
						{...commonProps}
						lookup={filteredLookup}
						defaultChecked={defaultValue}
					/>
				);
			case "Select":
				return (
					<Select
						{...commonProps}
						lookup={filteredLookup}
						options={options}
					/>
				);
			case "RadioGroup":
				return <RadioGroup {...commonProps} lookup={filteredLookup} />;
			case "Label":
				return (
					<Label
						defaultValue={
							defaultValue ||
							defaultValue ||
							commonProps.defaultValue
						}
					/>
				);
			case "TreeSelect":
				return <TreeSelect {...commonProps} tree={tree} />;
			case "Attachment":
				return (
					<FileUploader
						{...commonProps}
						attachmentDispatch={attachmentDispatch}
						isFormNew={isFormNew}
						saveFormDispatch={saveFormDispatch}
						formId={formId}
					/>
				);
			case "Cascader":
				return <Cascader {...commonProps} tree={tree} />;
			case "AutoComplete":
				return (
					<AutoComplete {...commonProps} lookup={filteredLookup} />
				);
			case "NumberDisplay":
				return (
					<NumberDisplay
						currency={dataType === "currency"}
						number={defaultValue}
					/>
				);
			case "TotalFundingSource":
				return <TotalFundingSource />;
			case "TotalFundingDifference":
				return <TotalFundingDifference />;
			case "AssetRc":
				return <AssetRc {...commonProps} />;
			case "AssetCriticality":
				return <AssetCriticality />;
			case "OverallRiskAssetFailure":
				return <OverallRiskAssetFailure />;
			case "OverallRiskConsequences":
				return <OverallRiskConsequences />;
			case "Heading":
				return <></>;
			case "MajorCategory":
				return <MajorCategory {...commonProps} />;
			case "MinorCategory":
				return <MinorCategory {...commonProps} />;
			case "ProjectEndDate":
				return (
					<ProjectEndDate
						{...commonProps}
						format={
							dataType === "date"
								? DATE_FORMAT_STRING
								: DATE_TIME_FORMAT_STRING
						}
					/>
				);
			case "Agencies":
				return <Agencies />;
			default:
				return (
					<p>{`Error: this type of input is not supported yet! TYPE: ${inputType}`}</p>
				);
		}
	};

	return (
		<>
			<InputDecider />
			{(status === "validation error" ||
				status === "validation error dismissed") &&
				errors.length > 0 &&
				errors.map((e) => (
					<span
						key={e.message}
						className="qna-question-form-item-validation"
					>
						{e.message}
					</span>
				))}
		</>
	);
}, arePropsEqual);
